import {
  ChangeEventHandler,
  FocusEventHandler,
  HTMLInputTypeAttribute,
  KeyboardEventHandler,
  memo,
  ReactNode,
} from "react";

import { FormControl } from "@mui/material";
import Box from "@mui/material/Box";
import Input from "@mui/joy/Input";
import FormLabel from "@mui/joy/FormLabel";

/**
 * Props for Text Field Component.
 * @interface
 * @name TextFieldProps
 * @kind interface
 */
interface TextFieldProps {
  /**
   * Standard HTML Attribute, the type of the input
   * (e.g.: `'text'`, `'tel'`, `'email'`, etc.).
   * @property
   * @name type
   * @kind property
   * @memberof TextFieldProps
   * @type {HTMLInputTypeAttribute | undefined}
   */
  type: HTMLInputTypeAttribute | undefined;
  /**
   * Id for the label for the Text Field Component.
   * @property
   * @name labelId
   * @kind property
   * @memberof TextFieldProps
   * @type {string}
   */
  labelId: string;
  /**
   * (optional)
   * Standard HTML Attribute, for label classes.
   * @property
   * @name labelClassName
   * @kind property
   * @memberof TextFieldProps
   * @type {string | undefined}
   */
  labelClassName?: string;
  /**
   * Label for the Text Field Component.
   * @property
   * @name label
   * @kind property
   * @memberof TextFieldProps
   * @type {ReactNode}
   */
  label: ReactNode;
  /**
   * Id for the Text Field Component.
   * @property
   * @name id
   * @kind property
   * @memberof TextFieldProps
   * @type {string}
   */
  id: string;
  /**
   * Callback fired when the value changes.
   * @property
   * @name onChange
   * @kind property
   * @memberof TextFieldProps
   * @type {ChangeEventHandler<HTMLInputElement>}
   */
  onChange: ChangeEventHandler<HTMLInputElement>;
  /**
   * (optional)
   * Standard Focus Event Attribute.
   * @property
   * @name onBlur
   * @kind property
   * @memberof TextFieldProps
   * @type {FocusEventHandler<HTMLInputElement> | undefined}
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * (optional)
   * Standard Keyboard Event Attribute.
   * @property
   * @name onKeyDown
   * @kind property
   * @memberof TextFieldProps
   * @type {KeyboardEventHandler<HTMLInputElement> | undefined}
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * Standard HTML Attribute, input value.
   * @property
   * @name value
   * @kind property
   * @memberof TextFieldProps
   * @type {string | number | readonly string[]}
   */
  value: string | number | readonly string[];
  /**
   * Standard HTML Attribute, input placeholder.
   * @property
   * @name placeholder
   * @kind property
   * @memberof TextFieldProps
   * @type {string}
   */
  placeholder: string;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof TextFieldProps
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * Standard HTML Attribute, for container classes.
   * @property
   * @name containerClassName
   * @kind property
   * @memberof TextFieldProps
   * @type {string | undefined}
   */
  containerClassName?: string;
  /**
   * (optional)
   * Standard HTML Attribute, maximum length number.
   * @property
   * @name maxLength
   * @kind property
   * @memberof TextFieldProps
   * @type {number | undefined}
   */
  maxLength?: number;
  /**
   * (optional)
   * Standard HTML Attribute, minimum length number.
   * @property
   * @name minLength
   * @kind property
   * @memberof TextFieldProps
   * @type {number | undefined}
   */
  minLength?: number;
  /**
   * (optional)
   * If `true`, the validation message is shown.
   * @property
   * @name hasError
   * @kind property
   * @memberof TextFieldProps
   * @type {boolean | undefined}
   */
  hasError?: boolean;
  /**
   * (optional)
   * Validation message.
   * @property
   * @name validationMessage
   * @kind property
   * @memberof TextFieldProps
   * @type {string | undefined}
   */
  validationMessage?: string;
  /**
   * (optional)
   * If `true`, disables the Text Field Component.
   * @property
   * @name disabled
   * @kind property
   * @memberof TextFieldProps
   * @type {boolean | undefined}
   */
  disabled?: boolean;

  readOnly?: boolean;
  helpText?: ReactNode;
  size?: "sm" | "md" | "lg";

  /**
   * (optional)
   * Trailing adornment for Text Field Component.
   * @property
   * @name endDecorator
   * @kind property
   * @memberof TextFieldProps
   * @type {React.ReactNode}
   */
  endDecorator?: React.ReactNode;
  /**
   * (optional)
   * Leading adornment for Text Field Component.
   * @property
   * @name startDecorator
   * @kind property
   * @memberof TextFieldProps
   * @type {React.ReactNode}
   */
  startDecorator?: React.ReactNode;

  inputRef?: React.Ref<HTMLInputElement>;

  /**
   * (optional)
   * Text colour of the value while disabled, for disabled fields that still
   * display information the user needs to read. Defaults to #DADADB.
   * @property
   * @name disabledTextColor
   * @kind property
   * @memberof TextFieldProps
   * @type {string}
   */
  disabledTextColor?: string;
}

/**
 * Memoized Text Field Component.
 * @constant
 * @name TextFieldComponent
 * @kind variable
 * @type {React.NamedExoticComponent<TextFieldProps>}
 */
const TextFieldComponent: React.NamedExoticComponent<TextFieldProps> = memo(
  /**
   * Text Field Component.
   * @function
   * @name TextFieldComponent
   * @kind function
   * @memberof TextFieldComponent
   * @param {TextFieldProps} props Props for Text Field Component.
   * @returns {JSX.Element} Text Field Component.
   */
  function TextFieldComponent(props: TextFieldProps): JSX.Element {
    const sizeHeight = { sm: 32, md: 40, lg: 48 }[props.size ?? "md"];

    return (
      <Box className={props.containerClassName}>
        <FormControl fullWidth>
          <FormLabel
            className={`label ${props.labelClassName}`}
            id={props.labelId}
            htmlFor={props.id}
          >
            {props.label}
          </FormLabel>
          <Input
            id={props.id}
            className={`text-field ${props.className} ${
              props.disabled ? "bg-[#F9F9F9]" : "bg-white"
            } [&:has(input:disabled)]:bg-[#F9F9F9] ${
              props.hasError ? "border-red-500" : "border-neutral-300"
            }`}
            placeholder={props.placeholder}
            value={props.value}
            onChange={props.onChange}
            onBlur={props.onBlur}
            onKeyDown={props.onKeyDown}
            slotProps={{
              input: {
                ref: props.inputRef,
                minLength: props.minLength,
                maxLength: props.maxLength,
                type: props.type,
                readOnly: props.readOnly,
              },
            }}
            sx={{
              "input:focus-visible": { outline: "none" },
              "input::placeholder": { color: "#C6C6C6" },
              "--Input-minHeight": `${sizeHeight}px`,
              "--Input-radius": "6px",
              "--Input-paddingInline": "16px",
              "--Input-focusedThickness": "1px",
              "&.Mui-focused": {
                "--Input-focusedHighlight": "#1A1C1F",
              },
              "&:hover:not(.Mui-focused):not(.Mui-disabled)": {
                borderColor: "#1A1C1F",
              },
              "&.Mui-disabled": {
                backgroundColor: "#F9F9F9",
                borderColor: "#ECECEC",
                "& input": {
                  color: props.disabledTextColor ?? "#DADADB",
                  WebkitTextFillColor: props.disabledTextColor ?? "#DADADB",
                },
              },
              "&:has(input:disabled)": {
                backgroundColor: "#F9F9F9",
              },
              ...(props.readOnly && {
                backgroundColor: "#F9F9F9",
                borderColor: "#DADADB",
              }),
              "& input[readonly]": {
                color: "#878785",
                WebkitTextFillColor: "#878785",
              },
            }}
            disabled={props.disabled}
            startDecorator={props.startDecorator}
            endDecorator={props.endDecorator}
          />

          {props.hasError && props.validationMessage && (
            <div className="ml-2 mt-1 text-xs text-red-400">
              {props.validationMessage}
            </div>
          )}
          {!props.hasError && props.helpText && (
            <div className="ml-2 mt-1 text-xs text-neutral-400">
              {props.helpText}
            </div>
          )}
        </FormControl>
      </Box>
    );
  }
);

export default TextFieldComponent;
